import moment from 'moment-timezone';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { askAssistant } from 'redux/slice/chatBubbleSlice';
import { getApi } from 'redux/apis';
import { AppDispatch } from 'redux/store';
import CapMark from 'components/Global/ChatBubble/CapMark';
import CapWordmark from 'components/Global/ChatBubble/CapWordmark';
import { EJournalStatus } from 'constants/Data';
import styles from './index.module.scss';

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

interface PayPeriodSummary {
	payPeriodId: string;
	payPeriodName: string;
	payPeriodStartDate?: string;
	payPeriodEndDate?: string;
	// Journal.amount is a String column in the database, so this arrives as a
	// string despite being money — everything downstream must coerce.
	amount?: number | string;
	// The related Journal's status, absent when no journal exists for the period.
	// DRAFT means a total has already been computed and saved (either via "Save
	// as Draft" or after a failed publish), so an unpublished period can still
	// carry a real amount.
	status?: number;
	isJournalPublished: boolean;
}

type Tone = 'action' | 'clear' | 'blocked';

interface LeadAction {
	text: string;
	run: () => void;
}

interface LeadItem {
	tone: Tone;
	label: string;
	// A lead reads as exactly one of these — a count, a dollar total, or a state
	// glyph — always in the same slot so the card's height doesn't change as the
	// underlying state does. `money` is its own field because a currency string
	// needs a smaller size than a one- or two-digit count to fit the same space.
	metric?: string;
	money?: string;
	glyph?: JSX.Element;
	headline: string;
	detail: string;
	primary: LeadAction;
	secondary?: LeadAction;
}

// The `$` prefix is hardcoded, so the grouping locale is pinned to match it —
// left to the browser's locale, a user in de-DE would be shown "$1.234.567".
const formatDollars = (value: unknown): string => {
	const num = Number(value);
	return `$${(Number.isFinite(num) ? num : 0).toLocaleString('en-US', {
		maximumFractionDigits: 0,
	})}`;
};

// payPeriodName from the API is a full "MM/DD/YYYY - MM/DD/YYYY" range — 23
// characters that read badly dropped inside a sentence. A brief refers to a
// period by the day it ends ("Aug 10"), resolved in the company's timezone
// the same way the rest of the dashboard renders pay period dates.
const formatPeriodEnd = (period?: PayPeriodSummary): string => {
	if (!period) return '';
	if (period.payPeriodEndDate) {
		return moment.tz(period.payPeriodEndDate, TIMEZONE).format('MMM D');
	}
	return period.payPeriodName || '';
};

const endedAt = (period: PayPeriodSummary): number =>
	period.payPeriodEndDate ? new Date(period.payPeriodEndDate).getTime() : 0;

const CheckGlyph = () => (
	<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
		<circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.6" />
		<path
			d="M7.5 12.4l3 3 6-6.4"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const AlertGlyph = () => (
	<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
		<circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.6" />
		<path d="M12 6.8v7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		<circle cx="12" cy="17.4" r="1.25" fill="currentColor" />
	</svg>
);

export default function AIBriefingStrip() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	// The assistant now lives only on the /chat page (the floating bubble was
	// removed), so an ask from the dashboard queues the prompt and navigates
	// there rather than opening a bubble in place.
	const onAsk = (message: string) => {
		dispatch(askAssistant(message));
		navigate('/chat');
	};

	const { selectedCompanyDetails } = useSelector((state: any) => state?.companies);
	const isConnected = selectedCompanyDetails?.company?.isConnected;

	// null = still loading; [] = loaded but empty (or no company yet) — kept
	// distinct so the cards can show a neutral "checking…" state instead of
	// briefly flashing an incorrect "all caught up" while this is in flight.
	const [periods, setPeriods] = useState<PayPeriodSummary[] | null>(null);
	// Hover opens the detail layer; this keeps it open once clicked, which is
	// the only way in for touch and keyboard users.
	const [pinned, setPinned] = useState(false);

	useEffect(() => {
		const companyId = localStorage.getItem('companyId');
		if (!companyId) {
			setPeriods([]);
			return;
		}
		// Switching companies twice in quick succession fires two overlapping
		// requests; without this guard the slower response wins and the brief
		// describes the company the user just navigated away from.
		let active = true;
		setPeriods(null);
		getApi('/dashboard/summary-by-payPeriod', { companyId })
			.then((res) => {
				if (active) setPeriods(res?.data?.data || []);
			})
			.catch(() => {
				if (active) setPeriods([]);
			});
		return () => {
			active = false;
		};
	}, [selectedCompanyDetails]);

	// The endpoint already orders by endDate desc, but the copy below claims
	// "latest" and "oldest" outright — sorting here makes those words true by
	// construction rather than by assumption about the query's orderBy.
	const list = useMemo(
		() => [...(periods || [])].sort((a, b) => endedAt(b) - endedAt(a)),
		[periods]
	);

	const loading = periods === null;
	const unpublished = list.filter((p) => !p.isJournalPublished);
	const latestUnpublished = unpublished[0];
	const oldestUnpublished = unpublished[unpublished.length - 1];
	const latestPublished = list.find((p) => p.isJournalPublished);
	const disconnected = isConnected === false;

	// An unpublished period whose journal is already drafted has a real computed
	// total sitting on it — the allocation work is done and only the posting is
	// outstanding. That is both more urgent and more concrete than a period that
	// hasn't been started, and it's the only case where a dollar figure for
	// unposted work is actually available client-side.
	const drafted = unpublished.filter(
		(p) => p.status === EJournalStatus.DRAFT && Number(p.amount) > 0
	);
	const draftedTotal = drafted.reduce((sum, p) => sum + Number(p.amount), 0);
	const notStartedCount = unpublished.length - drafted.length;

	// One lead item, chosen by severity: a broken QuickBooks connection outranks
	// a backlog because nothing can post until it's fixed, and a backlog outranks
	// the all-clear. Everything else in the strip is supporting detail.
	const lead: LeadItem = useMemo(() => {
		if (disconnected) {
			return {
				tone: 'blocked',
				label: 'Needs attention',
				glyph: <AlertGlyph />,
				headline: 'QuickBooks is disconnected',
				detail: "Allocations can't post until the connection is restored.",
				// Settings (where Integrations lives) is driven by menu keys rather
				// than router paths, so there is no URL to deep-link a reconnect to —
				// handing this to the assistant is the only real affordance here.
				primary: {
					text: 'How do I reconnect?',
					run: () => onAsk("My QuickBooks connection isn't working — how do I reconnect it?"),
				},
			};
		}
		// Drafted work outranks unstarted work: the numbers are already computed
		// and a real dollar figure is the strongest thing this strip can lead with.
		if (drafted.length > 0) {
			const many = drafted.length > 1;
			return {
				tone: 'action',
				label: 'Ready to post',
				money: formatDollars(draftedTotal),
				headline: `drafted across ${drafted.length} pay period${many ? 's' : ''}, not yet in QuickBooks`,
				detail:
					`Latest ends ${formatPeriodEnd(drafted[0])}` +
					(notStartedCount > 0
						? ` · ${notStartedCount} more period${notStartedCount > 1 ? 's' : ''} not allocated yet`
						: ''),
				// /journal-entries takes no pay-period param, so this lands on the
				// journals page rather than pretending to target one period.
				primary: { text: 'Review drafts', run: () => navigate('/journal-entries') },
				secondary: {
					text: 'Ask CAP',
					run: () =>
						onAsk(
							`I have ${formatDollars(draftedTotal)} drafted across ${drafted.length} pay period${many ? 's' : ''} that hasn't posted to QuickBooks. Can you walk me through publishing it?`
						),
				},
			};
		}
		if (unpublished.length > 0) {
			const many = unpublished.length > 1;
			return {
				tone: 'action',
				label: 'Needs you',
				metric: String(unpublished.length),
				headline: `pay period${many ? 's' : ''} not yet in QuickBooks`,
				detail: many
					? `Latest ends ${formatPeriodEnd(latestUnpublished)} · oldest still open ${formatPeriodEnd(oldestUnpublished)}`
					: `Period ends ${formatPeriodEnd(latestUnpublished)}`,
				primary: {
					text: `Open ${formatPeriodEnd(latestUnpublished)}`,
					// Deep-links to the page where the work actually happens. The key
					// must be `payPeriodId` — /employee-costs reads that one, while
					// /cost-allocations reads `payPeriod`.
					run: () =>
						navigate(`/employee-costs?payPeriodId=${latestUnpublished.payPeriodId}`),
				},
				secondary: {
					text: 'Ask CAP',
					run: () =>
						onAsk(
							`The pay period ending ${formatPeriodEnd(latestUnpublished)} hasn't been posted to QuickBooks yet. Can you walk me through allocating and publishing it?`
						),
				},
			};
		}
		// A company with no pay periods at all is not "all clear" — claiming every
		// period is posted when there are none reads as a bug to the user.
		if (!latestPublished) {
			return {
				tone: 'clear',
				label: 'Getting started',
				glyph: <CheckGlyph />,
				headline: 'No pay periods yet',
				detail: 'Once payroll data lands, this brief tracks what still needs posting.',
				primary: { text: 'Start an allocation', run: () => navigate('/employee-costs') },
				secondary: {
					text: 'Ask CAP',
					run: () => onAsk('I have no pay periods yet. How do I get started with cost allocation?'),
				},
			};
		}
		return {
			tone: 'clear',
			label: 'All clear',
			glyph: <CheckGlyph />,
			headline: 'Every recent pay period is posted',
			detail: `Most recent: ${formatPeriodEnd(latestPublished)}`,
			primary: {
				text: 'Review last posted',
				run: () => navigate(`/cost-allocations?payPeriod=${latestPublished.payPeriodId}`),
			},
			secondary: {
				text: 'Ask CAP',
				run: () =>
					onAsk(
						'All my recent pay periods look posted. What should I check before the next one comes in?'
					),
			},
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		disconnected,
		unpublished,
		latestUnpublished,
		oldestUnpublished,
		latestPublished,
		drafted,
		draftedTotal,
		notStartedCount,
		navigate,
	]);

	return (
		<section
			className={`${styles['brief']} ${pinned ? styles['brief--pinned'] : ''}`}
			aria-label="Your morning brief"
			aria-busy={loading}
		>
			{/* The collapsed state: one line that always shows the headline, so the
			    strip costs the dashboard ~56px instead of ~190px. Everything else
			    lives in the layer below, which opens on hover. It's a button as
			    well so touch and keyboard — neither of which can hover — can pin
			    it open. */}
			<button
				type="button"
				className={styles['brief__summary']}
				aria-expanded={pinned}
				onClick={() => setPinned((prev) => !prev)}
			>
				<span className={styles['brief__lockup']}>
					<CapWordmark height={16} />
					<CapMark size={13} tone="light" className={styles['brief__spark']} />
				</span>
				<span className={styles['brief__title']}>Your morning brief</span>
				{loading ? (
					<span className={styles['brief__digest-muted']}>Checking your pay periods…</span>
				) : (
					<>
						<span
							className={`${styles['brief__pulse']} ${styles[`brief__pulse--${lead.tone}`]}`}
						>
							{lead.label}
						</span>
						<span className={styles['brief__digest']}>
							{(lead.money || lead.metric) && (
								<strong>{lead.money || lead.metric}</strong>
							)}{' '}
							{lead.headline}
						</span>
					</>
				)}
				<span className={styles['brief__date']}>
					{moment().tz(TIMEZONE).format('ddd, MMM D')}
				</span>
				<span className={styles['brief__caret']} aria-hidden="true">
					⌄
				</span>
			</button>

			<div className={styles['brief__expand']}>
			<div className={styles['brief__expand-inner']}>
			<header className={styles['brief__intro']}>
				<div className={styles['brief__subtitle']}>
					Read from your pay periods. Nothing has been changed.
				</div>
				<button
					type="button"
					className={styles['brief__ask-link']}
					onClick={() => onAsk('Give me a rundown of where my allocations stand right now.')}
				>
					Ask about anything
					<span aria-hidden="true">›</span>
				</button>
			</header>

			<div className={styles['brief__body']}>
				<div
					className={`${styles['lead']} ${
						loading ? styles['lead--loading'] : styles[`lead--${lead.tone}`]
					}`}
					aria-live="polite"
				>
					{loading ? (
						// Deliberately neutral: the tone derived from an empty list would
						// read "Needs you" in the all-clear green before any data exists.
						<>
							<div className={styles['lead__label']}>Checking</div>
							<div className={styles['skeleton']} style={{ width: '62%', height: 26 }} />
							<div className={styles['skeleton']} style={{ width: '40%', height: 13 }} />
						</>
					) : (
						<>
							<div className={styles['lead__label']}>{lead.label}</div>
							<div className={styles['lead__headline']}>
								{lead.metric && (
									<span className={styles['lead__metric']}>{lead.metric}</span>
								)}
								{lead.money && (
									<span
										className={`${styles['lead__metric']} ${styles['lead__metric--money']}`}
									>
										{lead.money}
									</span>
								)}
								{lead.glyph && <span className={styles['lead__glyph']}>{lead.glyph}</span>}
								<span className={styles['lead__headline-text']}>{lead.headline}</span>
							</div>
							<div className={styles['lead__detail']}>{lead.detail}</div>
							<div className={styles['lead__actions']}>
								<button type="button" className={styles['btn-solid']} onClick={lead.primary.run}>
									{lead.primary.text}
								</button>
								{lead.secondary && (
									<button
										type="button"
										className={styles['btn-ghost']}
										onClick={lead.secondary.run}
									>
										{lead.secondary.text}
									</button>
								)}
							</div>
						</>
					)}
				</div>

				<div className={styles['brief__rail']}>
					<div className={styles['rail-item']}>
						<div className={styles['rail-item__label']}>Last posted</div>
						{loading ? (
							<div className={styles['skeleton']} style={{ width: '70%', height: 15 }} />
						) : latestPublished ? (
							<button
								type="button"
								className={styles['rail-item__value']}
								onClick={() =>
									navigate(`/cost-allocations?payPeriod=${latestPublished.payPeriodId}`)
								}
							>
								<span className={styles['rail-item__amount']}>
									{formatDollars(latestPublished.amount)}
								</span>
								<span className={styles['rail-item__meta']}>
									{formatPeriodEnd(latestPublished)}
								</span>
							</button>
						) : (
							<div className={styles['rail-item__empty']}>Nothing posted yet</div>
						)}
					</div>

					<div className={styles['rail-item']}>
						<div className={styles['rail-item__label']}>Reports</div>
						<button
							type="button"
							className={styles['rail-item__link']}
							onClick={() => navigate('/reports')}
						>
							Allocation summaries
							<span aria-hidden="true">›</span>
						</button>
					</div>
				</div>
			</div>
			</div>
			</div>
		</section>
	);
}
